import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import RequireStore from "@/components/RequireStore";
import { formatCurrency, MONTH_NAMES } from "@/lib/format";
import { formatDate } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Calendar, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function StoricoMesi() {
  const { storeId } = useStore();
  const [months, setMonths] = useState([]);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/months", { params: { store_id: storeId } }).then(({ data }) => setMonths(data));
  }, [storeId]);

  const openMonth = async (y, m) => {
    const { data } = await api.get(`/months/${y}/${m}`, { params: { store_id: storeId } });
    setDetail(data);
  };

  return (
    <RequireStore>
    <div className="space-y-6" data-testid="storico-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Storico Mesi</h1>
        <p className="text-sm text-muted-foreground">Tutti i mesi con dati. Clicca un mese per vedere i giorni.</p>
      </div>

      {months.length === 0 && (
        <div className="rounded-xl border border-border bg-card px-5 py-10 text-center text-muted-foreground">Nessun mese disponibile.</div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {months.map((mo) => (
          <button
            key={`${mo.year}-${mo.month}`}
            onClick={() => openMonth(mo.year, mo.month)}
            data-testid={`storico-month-${mo.year}-${mo.month}`}
            className="group rounded-xl border border-border bg-card p-5 text-left transition-colors duration-200 hover:border-primary/50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <span className="font-display text-lg font-bold">{mo.month_name} {mo.year}</span>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">Incassi</span>
              <span className="text-right tabular-nums">{formatCurrency(mo.totals.incasso)}</span>
              <span className="text-muted-foreground">Versamenti</span>
              <span className="text-right tabular-nums">{formatCurrency(mo.totals.versato)}</span>
              <span className="text-muted-foreground">Bar / Shop</span>
              <span className="text-right tabular-nums">{formatCurrency(mo.totals.bar + mo.totals.shop)}</span>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><ArrowDownRight className="h-3.5 w-3.5" /> Iniziale {formatCurrency(mo.saldo_iniziale)}</span>
              <span className="flex items-center gap-1 text-sm font-bold text-primary"><ArrowUpRight className="h-4 w-4" /> {formatCurrency(mo.saldo_finale)}</span>
            </div>
          </button>
        ))}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="storico-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.month_name} {detail.year}</DialogTitle>
              </DialogHeader>
              <div className="mb-2 flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                <span>Saldo iniziale: <b className="tabular-nums">{formatCurrency(detail.saldo_iniziale)}</b></span>
                <span>Saldo finale: <b className="tabular-nums text-primary">{formatCurrency(detail.saldo_finale)}</b></span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                      <th className="py-2 pr-2">Data</th>
                      <th className="py-2 px-2 text-right">Incasso</th>
                      <th className="py-2 px-2 text-right">Versato</th>
                      <th className="py-2 pl-2 text-right">Da versare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.days.map((d) => (
                      <tr key={d.date} className="border-b border-border/40">
                        <td className="py-2 pr-2 whitespace-nowrap">{formatDate(d.date)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(d.incasso_contanti)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-[#3B9BFF]">{formatCurrency(d.versato)}</td>
                        <td className="py-2 pl-2 text-right font-semibold tabular-nums text-primary">{formatCurrency(d.da_versare)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-primary/30 font-bold">
                      <td className="py-2 pr-2">TOTALI</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(detail.totals.incasso)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(detail.totals.versato)}</td>
                      <td className="py-2 pl-2 text-right tabular-nums text-primary">{formatCurrency(detail.saldo_finale)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </RequireStore>
  );
}
